import type { CalendarEvent, ConflictResult, ConflictRule } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';

/**
 * Detects conflicts between scheduled persons and absence calendar events.
 *
 * For each calendar event of type 'absence', checks if the person it applies to
 * (the owner of the personal calendar) is scheduled on that date.
 *
 * Absence events are scoped to the individual person (unlike holidays which are
 * location-scoped). Each absence event has exactly one person in `applicablePersonIds`.
 *
 * Returns a ConflictResult per person-per-absence overlap.
 */
export class AbsenceConflictRule implements ConflictRule {
  readonly type = 'absence' as const;

  detect(schedule: ScheduleMap, events: CalendarEvent[]): ConflictResult[] {
    const results: ConflictResult[] = [];

    const absenceEvents = events.filter(e => e.type === 'absence');

    for (const event of absenceEvents) {
      const daySchedule = schedule[event.date];
      if (!daySchedule) continue;

      const applicableSet = new Set(event.applicablePersonIds);

      for (const personId of Object.keys(daySchedule)) {
        if (applicableSet.has(personId)) {
          results.push({
            type: 'absence',
            date: event.date,
            personId,
            eventLabel: event.label,
            calendarId: event.calendarId,
          });
        }
      }
    }

    return results;
  }
}
